import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/** Every step, in order. Changing a choice clears the choices after it. */
const ORDER = ['type', 'model', 'color', 'size', 'deco', 'upload', 'notes', 'qty'];
const VECTOR_TYPES = ['svg', 'ai', 'eps', 'pdf'];
const RASTER_TYPES = ['png', 'jpg', 'jpeg'];

/**
 * @typedef {object} Choice
 * @property {string} value - The name shown to the customer and saved on the order.
 * @property {string} [id] - The Shopify option value id, for color, size and decoration.
 * @property {string} [url] - The product URL, for the style.
 * @property {boolean} [auto] - True when the size was set because the color has only one.
 */

/**
 * One artwork file field. A placement with two locations has two of these.
 * @typedef {object} Upload
 * @property {string} location - For example "Full Back".
 * @property {string} property - The line item property name, for example "Back artwork".
 * @property {HTMLElement} element
 * @property {HTMLInputElement} input
 * @property {boolean} vectorize - True when the service was added for this file.
 */

/**
 * One line of the quantity step: a color and size of the chosen garment, with the
 * variant Shopify resolved for it and the chosen placement.
 * @typedef {object} Row
 * @property {string} colorId
 * @property {string} colorName
 * @property {string} sizeId - '' until the customer picks a size.
 * @property {string} sizeName
 * @property {{ id: string, name: string }[]} sizes - The sizes that exist for the color.
 * @property {string} variantId - '' until a size is picked.
 * @property {number} cents - The variant's price in minor units.
 * @property {number} quantity
 */

/**
 * @typedef {object} ApparelBuilderRefs
 * @property {HTMLFormElement} form
 * @property {HTMLElement} [fallback]
 * @property {HTMLElement} chips
 * @property {HTMLElement} [empty]
 * @property {HTMLElement} price
 * @property {HTMLElement} [priceNote]
 * @property {HTMLElement} stage
 * @property {HTMLElement} progress
 * @property {HTMLElement} status
 * @property {HTMLElement} uploads
 * @property {HTMLTemplateElement} uploadTemplate
 * @property {HTMLElement} embroideryFiles
 * @property {HTMLInputElement} stitchFile
 * @property {HTMLInputElement} pdfFile
 * @property {HTMLElement} stitchMessage
 * @property {HTMLElement} pdfMessage
 * @property {HTMLElement} [digitizeBox]
 * @property {HTMLButtonElement} [digitize]
 * @property {HTMLTextAreaElement} notes
 * @property {HTMLElement} rows
 * @property {HTMLButtonElement} addRow
 * @property {HTMLElement} total
 * @property {HTMLTemplateElement} rowTemplate
 * @property {HTMLTemplateElement} [moneyFormat]
 * @property {HTMLFieldSetElement} [stock]
 * @property {HTMLButtonElement} submit
 * @property {HTMLButtonElement} back
 * @property {HTMLButtonElement} next
 * @property {HTMLElement} fields
 */

/**
 * Step-by-step apparel builder, modeled on the hat builder (assets/hat-builder.js) but
 * kept separate from it. Colors, sizes, placements, prices and variant ids are read from
 * sections/hat-builder-product.liquid through the Section Rendering API, so a combination
 * with no variant is never offered and never sent to the cart.
 *
 * @extends {Component<ApparelBuilderRefs>}
 */
class ApparelBuilder extends Component {
  requiredRefs = [
    'form', 'chips', 'price', 'stage', 'progress', 'status', 'uploads', 'uploadTemplate', 'embroideryFiles',
    'stitchFile', 'pdfFile', 'notes', 'rows', 'addRow', 'total', 'rowTemplate', 'submit', 'back', 'next', 'fields',
  ];

  /** @type {Record<string, Choice | undefined>} */
  #state = {};
  #current = 0;
  /** Option value ids sent to Shopify to resolve variants. */
  #ids = { color: '', size: '', deco: '' };
  #hasSizeOption = false;
  /** The sizes that exist for the chosen color. @type {{ id: string, name: string }[]} */
  #colorSizes = [];
  /** The variant for the chosen color, size and placement. @type {{ id: string, price: string, cents: number } | null} */
  #variant = null;
  /** @type {Upload[]} */
  #uploads = [];
  /** Embroidery only: the service, offered as "digitizing" in place of the stitch file and PDF. */
  #digitize = false;
  /** @type {Row[]} */
  #rows = [];
  #busy = false;
  #sending = false;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #sendingTimer;
  /** @type {AbortController | undefined} */
  #listeners;
  /** @type {AbortController | undefined} */
  #request;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    const { form, fallback } = this.refs;

    this.addEventListener('click', this.#onClick, { signal });
    form.addEventListener('submit', this.#onSubmit, { signal });
    form.addEventListener('change', this.#onChange, { signal });
    form.addEventListener('input', this.#onInput, { signal });
    // Coming back with the browser's back button: allow sending again.
    window.addEventListener('pageshow', () => this.#setSending(false), { signal });

    if (fallback) fallback.hidden = true;
    form.hidden = false;
    this.#decorateTypeTiles();

    const params = new URLSearchParams(window.location.search);
    const wanted = params.get('model');
    const tile = wanted ? this.#tiles('model').find((candidate) => candidate.dataset.handle === wanted) : undefined;
    // ?type=<type name> opens on that garment type's styles. Upper and lower case do not matter.
    const wantedType = (params.get('type') ?? '').trim().toLowerCase();
    const typeTile = wantedType ? this.#tiles('type').find((candidate) => (candidate.dataset.value ?? '').toLowerCase() === wantedType) : undefined;
    if (tile?.dataset.type) {
      this.#state.type = { value: tile.dataset.type };
      this.#choose(tile);
    } else if (typeTile) {
      this.#choose(typeTile);
    } else {
      this.#render(false);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#request?.abort();
    clearTimeout(this.#sendingTimer);
  }

  /** Each garment type tile borrows the photo of its first style. */
  #decorateTypeTiles() {
    for (const tile of this.#tiles('type')) {
      if (tile.querySelector('img')) continue;
      const image = this.#tiles('model')
        .find((model) => model.dataset.type === tile.dataset.value)
        ?.querySelector('img');
      if (image) tile.prepend(image.cloneNode(true));
    }
  }

  /**
   * @param {string} key
   * @returns {HTMLElement | null}
   */
  #panel(key) {
    return this.querySelector(`[data-panel="${key}"]`);
  }

  /**
   * @param {string} key
   * @returns {HTMLElement[]} Every tile of a step, shown or not.
   */
  #tiles(key) {
    return Array.from(this.querySelectorAll(`.hat-builder__tile[data-step="${key}"]`));
  }

  /** @returns {boolean} True when the chosen placement is embroidery (the polos). */
  #embroidery() {
    return (this.#state.deco?.value ?? '').toLowerCase().includes('embroidery');
  }

  /**
   * The tiles a customer may pick on a step.
   * @param {string} key
   * @returns {HTMLElement[]}
   */
  #choices(key) {
    const tiles = this.#tiles(key);
    if (key === 'model') return tiles.filter((tile) => tile.dataset.type === this.#state.type?.value);
    // A size or placement with no purchasable variant for the choices so far is left out.
    if (key === 'size' || key === 'deco') return tiles.filter((tile) => this.#purchasable(tile));
    return tiles;
  }

  /** @returns {string[]} The steps that apply. The size step is skipped when there is nothing to choose. */
  #steps() {
    const state = this.#state;
    const sized = state.color ? this.#colorSizes.length > 1 : !state.model || this.#hasSizeOption;
    return ORDER.filter((key) => key !== 'size' || sized);
  }

  /**
   * @param {string} key
   * @returns {boolean} Notes are optional; every other step needs an answer.
   */
  #answered(key) {
    return key === 'notes' || Boolean(this.#state[key]);
  }

  /** @param {string} key */
  #clearAfter(key) {
    for (const later of ORDER.slice(ORDER.indexOf(key) + 1)) delete this.#state[later];
    if (ORDER.indexOf(key) < ORDER.indexOf('deco')) this.#variant = null;
    this.#rows = [];
    this.refs.rows.replaceChildren();
    this.#digitize = false;
    this.#uploads = [];
    this.refs.uploads.replaceChildren();
    for (const input of [this.refs.stitchFile, this.refs.pdfFile]) input.value = '';
  }

  /** @param {HTMLElement} tile */
  async #choose(tile) {
    const key = tile.dataset.step;
    const value = tile.dataset.value;
    if (this.#busy || !key || !value) return;

    const changed = this.#state[key]?.value !== value;
    if (changed) {
      this.#clearAfter(key);
      this.#state[key] = { value, id: tile.dataset.optionValueId, url: tile.dataset.url };

      try {
        if (key === 'model') await this.#loadModel();
        if (key === 'color') await this.#loadColor();
        if (key === 'size') await this.#loadSize();
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        delete this.#state[key];
        this.#setBusy(false);
        this.#showStatus(error instanceof Error && error.message === 'unavailable' ? 'unavailable' : 'error');
        this.#render(false);
        return;
      }

      if (key === 'deco') {
        this.#ids.deco = tile.dataset.optionValueId ?? '';
        this.#variant = {
          id: tile.dataset.variantId ?? '',
          price: tile.dataset.price ?? '',
          cents: Number(tile.dataset.priceCents) || 0,
        };
        this.#buildUploads();
      }
    }

    this.#showStatus('');
    const steps = this.#steps();
    this.#current = Math.min(steps.indexOf(key) + 1, steps.length - 1);
    this.#render(true);
  }

  /**
   * Ask Shopify for the product's option tiles, resolved for the given option values.
   * @param {string[]} ids
   * @returns {Promise<HTMLElement>}
   */
  async #fetchOptions(ids) {
    const model = this.#state.model;
    if (!model?.url) throw new Error('error');

    this.#request?.abort();
    this.#request = new AbortController();
    this.#setBusy(true);

    const url = new URL(model.url, window.location.origin);
    url.searchParams.set('section_id', this.dataset.section ?? '');
    const selected = ids.filter(Boolean);
    if (selected.length) url.searchParams.set('option_values', selected.join(','));

    const response = await fetch(url, { signal: this.#request.signal });
    if (!response.ok) throw new Error('error');
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const root = html.querySelector('.hat-builder-product');
    if (!(root instanceof HTMLElement)) throw new Error('error');

    this.#setBusy(false);
    return root;
  }

  /**
   * @param {HTMLElement} root
   * @param {string} option - "color", "size" or "deco".
   * @returns {HTMLElement[]}
   */
  #optionTiles(root, option) {
    return /** @type {HTMLElement[]} */ (Array.from(root.querySelectorAll(`[data-option="${option}"] .hat-builder__tile`)));
  }

  /**
   * @param {HTMLElement} tile
   * @returns {boolean} True when the tile's combination exists and can be bought.
   */
  #purchasable(tile) {
    return Boolean(tile.dataset.variantId) && tile.dataset.variantAvailable === 'true';
  }

  /**
   * @param {HTMLElement} root
   * @returns {{ id: string, name: string }[]} The sizes that exist for the color the tiles were resolved for.
   */
  #sizesIn(root) {
    return this.#optionTiles(root, 'size')
      .filter((tile) => this.#purchasable(tile))
      .map((tile) => ({ id: tile.dataset.optionValueId ?? '', name: tile.dataset.value ?? '' }));
  }

  /**
   * Replace a step's tiles with the ones Shopify rendered.
   * @param {HTMLElement} root
   * @param {'color' | 'size' | 'deco'} key
   */
  #fill(root, key) {
    const box = this.querySelector(`[data-tiles="${key}"]`);
    if (!box) return;
    box.replaceChildren(...this.#optionTiles(root, key).map((node) => document.importNode(node, true)));
  }

  /**
   * @param {HTMLElement} root
   * @param {string} option
   * @returns {string} The id of the option value Shopify treats as selected.
   */
  #selectedId(root, option) {
    return this.#optionTiles(root, option).find((tile) => tile.dataset.selected === 'true')?.dataset.optionValueId ?? '';
  }

  async #loadModel() {
    const root = await this.#fetchOptions([]);
    if (!this.#optionTiles(root, 'color').length || !this.#optionTiles(root, 'deco').length) throw new Error('error');

    this.#fill(root, 'color');
    this.#fill(root, 'size');
    this.#fill(root, 'deco');
    this.#ids = {
      color: this.#selectedId(root, 'color'),
      size: this.#selectedId(root, 'size'),
      deco: this.#selectedId(root, 'deco'),
    };
    this.#hasSizeOption = this.#optionTiles(root, 'size').length > 0;
    this.#colorSizes = [];
  }

  /** Reads which sizes exist for the chosen color. A color made in one size gets that size. */
  async #loadColor() {
    this.#ids.color = this.#state.color?.id ?? '';
    this.#colorSizes = [];
    await this.#refreshOptions();
    if (!this.#hasSizeOption) {
      if (!this.#choices('deco').length) throw new Error('unavailable');
      return;
    }

    const sizes = this.#choices('size').map((tile) => ({ id: tile.dataset.optionValueId ?? '', name: tile.dataset.value ?? '' }));
    const only = sizes.length === 1 ? sizes[0] : undefined;
    if (!sizes.length) throw new Error('unavailable');
    this.#colorSizes = sizes;
    // With several sizes the customer chooses next, and that choice loads the placements.
    if (!only) return;

    this.#state.size = { value: only.name, id: only.id, auto: true };
    if (only.id !== this.#ids.size) {
      this.#ids.size = only.id;
      await this.#refreshOptions();
    }
    if (!this.#choices('deco').length) throw new Error('unavailable');
  }

  /** Loads the placements, with their exact prices, for the chosen color and size. */
  async #loadSize() {
    this.#ids.size = this.#state.size?.id ?? '';
    await this.#refreshOptions();
    if (!this.#choices('deco').length) throw new Error('unavailable');
  }

  async #refreshOptions() {
    const { color, size, deco } = this.#ids;
    const root = await this.#fetchOptions([color, size, deco]);
    this.#fill(root, 'size');
    this.#fill(root, 'deco');
  }

  /**
   * "DTF - Full Back + Left Chest" has the locations "Full Back" and "Left Chest".
   * @returns {string[]}
   */
  #locations() {
    const name = this.#state.deco?.value ?? '';
    const after = name.includes(' - ') ? name.slice(name.indexOf(' - ') + 3) : name;
    return after
      .split('+')
      .map((part) => part.trim())
      .filter(Boolean);
  }

  /**
   * "Full Back" is labeled and saved as "Back artwork".
   * @param {string} location
   */
  #artworkName(location) {
    const base = location.replace(/^full\s+/i, '').toLowerCase();
    const label = base.charAt(0).toUpperCase() + base.slice(1);
    return (this.dataset.artworkLabel ?? '[location] artwork').replace('[location]', label);
  }

  /** One artwork field per location of the chosen placement. */
  #buildUploads() {
    const { uploads, uploadTemplate } = this.refs;
    this.#uploads = [];
    const elements = this.#locations().map((location, index) => {
      const element = /** @type {HTMLElement} */ (uploadTemplate.content.firstElementChild?.cloneNode(true));
      const input = /** @type {HTMLInputElement} */ (element.querySelector('[data-upload-input]'));
      const label = element.querySelector('[data-upload-label]');
      const property = this.#artworkName(location);
      input.id = `${this.refs.stitchFile.id}-upload-${index}`;
      if (label instanceof HTMLLabelElement) {
        label.textContent = property;
        label.htmlFor = input.id;
      }
      element.dataset.upload = String(index);
      this.#uploads.push({ location, property, element, input, vectorize: false });
      return element;
    });
    uploads.replaceChildren(...elements);
  }

  /**
   * @param {HTMLInputElement} input
   * @returns {string} The chosen file's extension in lower case, or '' when no file is chosen.
   */
  #extension(input) {
    const file = input.files?.[0];
    return file ? (file.name.split('.').pop() ?? '').toLowerCase() || 'unknown' : '';
  }

  /** @param {string} type */
  #accepted(type) {
    return VECTOR_TYPES.includes(type) || RASTER_TYPES.includes(type);
  }

  /** @returns {boolean} True when embroidery still asks for the stitch file and the PDF. */
  #needsEmbroideryFiles() {
    return this.#embroidery() && !this.#digitize;
  }

  /** Works out whether the upload step is answered, and what its chip says. */
  #syncUpload() {
    const { stitchFile, pdfFile } = this.refs;
    if (!this.#state.deco) {
      delete this.#state.upload;
      return;
    }

    if (this.#needsEmbroideryFiles()) {
      const complete = this.#extension(stitchFile) === 'dst' && this.#extension(pdfFile) === 'pdf';
      this.#state.upload = complete ? { value: `${stitchFile.files?.[0]?.name}, ${pdfFile.files?.[0]?.name}` } : undefined;
      return;
    }

    const complete = this.#uploads.length > 0 && this.#uploads.every((upload) => this.#accepted(this.#extension(upload.input)));
    this.#state.upload = complete ? { value: this.#uploads.map((upload) => upload.input.files?.[0]?.name ?? '').join(', ') } : undefined;
  }

  /**
   * The service lines to add: one per raster file the customer asked to have vectorized,
   * or one for embroidery with digitizing.
   * @returns {string[]} The location each service line is for.
   */
  #services() {
    if (!this.dataset.serviceVariantId) return [];
    if (this.#embroidery()) return this.#digitize ? this.#locations().slice(0, 1) : [];
    return this.#uploads
      .filter((upload) => upload.vectorize && RASTER_TYPES.includes(this.#extension(upload.input)))
      .map((upload) => upload.location);
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    this.refs.stage.setAttribute('aria-busy', String(busy));
    if (busy) this.#showStatus('loading');
  }

  /**
   * Blocks a second send while the first is uploading. If the browser never leaves the
   * page (for example the connection dropped), sending is allowed again after a while.
   * @param {boolean} sending
   */
  #setSending(sending) {
    clearTimeout(this.#sendingTimer);
    this.#sending = sending;
    this.#showStatus(sending ? 'sending' : '');
    // The out-of-stock answer travels as a line item property, so the radios are not posted.
    for (const radio of this.#stockRadios()) radio.disabled = sending;
    if (sending) this.#sendingTimer = setTimeout(() => this.#setSending(false), 30000);
  }

  /** @returns {HTMLInputElement[]} */
  #stockRadios() {
    return Array.from(this.refs.stock?.querySelectorAll('input[data-stock-choice]') ?? []);
  }

  /** @returns {string} The chosen "If out of stock" answer, or '' when none is chosen yet. */
  #stockChoice() {
    return this.#stockRadios().find((radio) => radio.checked)?.value ?? '';
  }

  /** @param {'' | 'loading' | 'error' | 'unavailable' | 'sending'} kind */
  #showStatus(kind) {
    const { status } = this.refs;
    status.textContent = kind ? status.dataset[kind] ?? '' : '';
    status.hidden = !kind;
  }

  /** @returns {boolean} True only when every step is answered and every row is a real, purchasable variant. */
  #ready() {
    return (
      this.#steps().every((key) => this.#answered(key)) &&
      this.#rows.length > 0 &&
      this.#rows.every((row) => row.variantId && row.quantity >= 1) &&
      // When the out-of-stock question is shown, it must be answered.
      (!this.refs.stock || this.#stockChoice() !== '') &&
      !this.#busy
    );
  }

  /** @param {boolean} moved - True when the step changed, to slide it in and move focus. */
  #render(moved) {
    const state = this.#state;
    const refs = this.refs;
    this.#syncUpload();

    const notes = refs.notes.value.trim();
    state.notes = notes ? { value: notes } : undefined;

    const steps = this.#steps();
    this.#current = Math.min(this.#current, steps.length - 1);
    const key = steps[this.#current] ?? 'type';
    // The quantity step starts with one row: the color and size already chosen.
    if (key === 'qty' && !this.#rows.length) this.#startRows();

    for (const panel of this.querySelectorAll('[data-panel]')) {
      if (!(panel instanceof HTMLElement)) continue;
      const active = panel.dataset.panel === key;
      panel.hidden = !active;
      panel.removeAttribute('data-enter');
      if (active && moved) {
        // Restart the slide-in animation.
        void panel.offsetWidth;
        panel.setAttribute('data-enter', '');
        const heading = panel.querySelector('.apparel-builder__panel-heading');
        if (heading instanceof HTMLElement) heading.focus();
      }
    }

    for (const step of ['type', 'model', 'color', 'size', 'deco']) {
      const allowed = new Set(this.#choices(step));
      for (const tile of this.#tiles(step)) {
        tile.hidden = !allowed.has(tile);
        tile.setAttribute('aria-pressed', String(state[step]?.value === tile.dataset.value));
      }
    }

    this.#renderUploads();
    this.#renderChips();

    refs.progress.replaceChildren(
      ...steps.map((step) => {
        const segment = document.createElement('span');
        if (state[step] || (step === 'notes' && this.#current > steps.indexOf('notes'))) segment.setAttribute('data-done', '');
        return segment;
      })
    );

    // The exact variant price for the chosen color, size and placement.
    refs.price.textContent = this.#variant?.price || '—';

    this.#updateRows();
    refs.back.disabled = this.#current === 0;
    refs.next.disabled = !this.#answered(key) || this.#current >= steps.length - 1;
  }

  /** Shows the right file fields and messages for the chosen placement. */
  #renderUploads() {
    const refs = this.refs;
    const embroidery = this.#embroidery();
    const twoFiles = this.#needsEmbroideryFiles();
    const hasService = Boolean(this.dataset.serviceVariantId);

    refs.embroideryFiles.hidden = !twoFiles;
    refs.uploads.hidden = twoFiles;
    if (refs.digitizeBox) refs.digitizeBox.hidden = !embroidery;
    const stitchType = this.#extension(refs.stitchFile);
    const pdfType = this.#extension(refs.pdfFile);
    refs.stitchMessage.hidden = !twoFiles || !stitchType || stitchType === 'dst';
    refs.pdfMessage.hidden = !twoFiles || !pdfType || pdfType === 'pdf';
    if (refs.digitize) this.#setToggle(refs.digitize, this.#digitize);

    for (const upload of this.#uploads) {
      const type = this.#extension(upload.input);
      const raster = RASTER_TYPES.includes(type);
      const show = (/** @type {string} */ selector, /** @type {boolean} */ visible) => {
        const element = upload.element.querySelector(selector);
        if (element instanceof HTMLElement) element.hidden = !visible;
      };
      // Embroidery talks about digitizing instead; its artwork field shows no vectorization wording.
      show('[data-upload-vector]', !embroidery && VECTOR_TYPES.includes(type));
      show('[data-upload-raster]', !embroidery && raster);
      show('[data-upload-type]', Boolean(type) && !this.#accepted(type));
      show('[data-upload-vectorize]', !embroidery && raster && hasService);
      show('[data-upload-notice]', !embroidery && raster && hasService);
      const toggle = upload.element.querySelector('[data-upload-vectorize]');
      if (toggle instanceof HTMLElement) this.#setToggle(toggle, upload.vectorize);
    }

    if (refs.priceNote) {
      const data = this.dataset;
      const added = this.#services().length > 0;
      const note = embroidery
        ? added
          ? data.priceNoteDigitizing
          : data.priceNoteEmbroidery
        : added
        ? data.priceNoteVectorization
        : data.priceNote;
      refs.priceNote.textContent = note ?? '';
    }
    for (const notice of this.querySelectorAll('[data-notice-for]')) {
      if (notice instanceof HTMLElement) notice.hidden = (notice.dataset.noticeFor === 'embroidery') !== embroidery;
    }
  }

  /**
   * @param {HTMLElement} toggle
   * @param {boolean} on
   */
  #setToggle(toggle, on) {
    toggle.setAttribute('aria-pressed', String(on));
    for (const label of toggle.querySelectorAll('span')) label.hidden = label.hasAttribute('data-on') !== on;
  }

  /** The customer's choices so far. Tapping one goes back to that step. */
  #renderChips() {
    const chips = [];
    for (const key of ORDER) {
      const choice = this.#state[key];
      if (!choice) continue;

      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'apparel-builder__chip';
      // A size set automatically has no step of its own; its chip goes back to the color.
      chip.dataset.chip = this.#steps().includes(key) ? key : 'color';

      const label = document.createElement('span');
      label.className = 'apparel-builder__chip-label';
      label.textContent = this.#panel(key)?.dataset.label ?? '';
      const value = document.createElement('span');
      value.className = 'apparel-builder__chip-value';
      value.textContent = choice.value;
      chip.append(label, value);
      chips.push(chip);
    }
    this.refs.chips.replaceChildren(...chips);
    if (this.refs.empty) this.refs.empty.hidden = chips.length > 0;
  }

  /**
   * @param {Element} element - Anything inside a quantity row.
   * @returns {number} The row's position in the list.
   */
  #rowIndex(element) {
    const row = element.closest('.apparel-builder__row');
    return row instanceof HTMLElement ? Number(row.dataset.row) : -1;
  }

  /** @param {number} cents */
  #money(cents) {
    const format = this.refs.moneyFormat?.content.textContent || '{{amount}}';
    return formatMoney(cents, format, this.dataset.currency ?? '');
  }

  /** The first row is the color and size chosen in the steps, at quantity 1. */
  #startRows() {
    const { color, size } = this.#state;
    if (!this.#variant || !color) return;
    this.#rows = [
      {
        colorId: color.id ?? '',
        colorName: color.value,
        sizeId: size?.id ?? '',
        sizeName: size?.value ?? '',
        sizes: this.#colorSizes,
        variantId: this.#variant.id,
        cents: this.#variant.cents,
        quantity: 1,
      },
    ];
    this.#buildRows();
  }

  /**
   * Ask Shopify which sizes exist for a color, and which variant matches the color, the
   * size and the chosen placement. A size the color is not made in is cleared, so a row
   * can never hold a combination without a variant.
   * @param {string} colorId
   * @param {string} sizeId - '' when no size is chosen yet.
   * @returns {Promise<Omit<Row, 'quantity'>>}
   */
  async #resolveRow(colorId, sizeId) {
    const decoId = this.#ids.deco;
    // Without a size, the chosen size is sent: the sizes listed do not depend on it.
    const root = await this.#fetchOptions([colorId, sizeId || this.#ids.size, decoId]);

    /** @type {{ id: string, name: string }[]} */
    let sizes = [];
    if (this.#hasSizeOption) {
      sizes = this.#sizesIn(root);
      if (!sizes.length) throw new Error('unavailable');
      if (!sizes.some((size) => size.id === sizeId)) sizeId = '';
    }

    const decoration = this.#optionTiles(root, 'deco').find((tile) => tile.dataset.optionValueId === decoId);
    const chosen = !this.#hasSizeOption || sizeId !== '';
    if (chosen && (!decoration || !this.#purchasable(decoration))) throw new Error('unavailable');

    return {
      colorId,
      colorName: this.#tiles('color').find((tile) => tile.dataset.optionValueId === colorId)?.dataset.value ?? '',
      sizeId,
      sizeName: sizes.find((size) => size.id === sizeId)?.name ?? '',
      sizes,
      variantId: chosen ? decoration?.dataset.variantId ?? '' : '',
      cents: chosen ? Number(decoration?.dataset.priceCents) || 0 : 0,
    };
  }

  /**
   * Loads a row's sizes and variant for a color and size, then redraws the rows.
   * @param {number} index - The row to change, or the list's length to add a row.
   * @param {string} colorId
   * @param {string} sizeId
   * @param {string} focus - The control to focus in that row afterwards.
   */
  async #loadRow(index, colorId, sizeId, focus) {
    if (this.#busy) return;
    try {
      const resolved = await this.#resolveRow(colorId, sizeId);
      this.#rows[index] = { ...resolved, quantity: this.#rows[index]?.quantity ?? 1 };
      this.#showStatus('');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      this.#setBusy(false);
      this.#showStatus(error instanceof Error && error.message === 'unavailable' ? 'unavailable' : 'error');
    }
    this.#buildRows();
    this.#renderChips();
    const control = this.refs.rows.querySelector(`[data-row="${index}"] ${focus}`);
    if (control instanceof HTMLElement) control.focus();
  }

  /** Draws every row from the template. Called when rows are added, removed or reloaded. */
  #buildRows() {
    const { rows, rowTemplate, stitchFile } = this.refs;
    const colors = this.#tiles('color');
    const option = (/** @type {string} */ value, /** @type {string} */ text, /** @type {boolean} */ selected) => {
      const element = document.createElement('option');
      element.value = value;
      element.textContent = text;
      element.selected = selected;
      return element;
    };

    rows.replaceChildren(
      ...this.#rows.map((row, index) => {
        const element = /** @type {HTMLElement} */ (rowTemplate.content.firstElementChild?.cloneNode(true));
        const first = index === 0;
        element.dataset.row = String(index);

        /** @type {Record<string, HTMLElement | null>} */
        const controls = {
          color: element.querySelector('[data-row-color]'),
          size: element.querySelector('[data-row-size]'),
          quantity: element.querySelector('[data-row-quantity]'),
        };
        const colorText = element.querySelector('[data-row-color-text]');
        const sizeText = element.querySelector('[data-row-size-text]');
        const sizeField = element.querySelector('[data-row-size-field]');
        const remove = element.querySelector('[data-row-remove]');

        // The first row is the color and size chosen in the steps, shown as text.
        if (colorText instanceof HTMLElement) {
          colorText.textContent = row.colorName;
          colorText.hidden = !first;
        }
        if (sizeText instanceof HTMLElement) {
          sizeText.textContent = row.sizeName;
          sizeText.hidden = !first;
        }
        if (controls.color) {
          controls.color.hidden = first;
          if (!first) {
            controls.color.append(
              ...colors.map((tile) => option(tile.dataset.optionValueId ?? '', tile.dataset.value ?? '', tile.dataset.optionValueId === row.colorId))
            );
          }
        }
        if (controls.size) {
          controls.size.hidden = first;
          // Only the sizes that exist for the row's color are offered.
          if (row.sizes.length !== 1) controls.size.append(option('', this.dataset.sizePlaceholder ?? '', row.sizeId === ''));
          controls.size.append(...row.sizes.map((size) => option(size.id, size.name, size.id === row.sizeId)));
        }
        if (sizeField instanceof HTMLElement) sizeField.hidden = !this.#hasSizeOption;
        if (controls.quantity instanceof HTMLInputElement) controls.quantity.value = String(row.quantity);
        if (remove instanceof HTMLElement) remove.hidden = first;

        for (const label of element.querySelectorAll('[data-row-label]')) {
          const control = label instanceof HTMLLabelElement ? controls[label.dataset.rowLabel ?? ''] : null;
          if (!control || !(label instanceof HTMLLabelElement)) continue;
          control.id = `${stitchFile.id}-row-${index}-${label.dataset.rowLabel}`;
          if (!control.hidden) label.htmlFor = control.id;
        }
        return element;
      })
    );
    this.#updateRows();
  }

  /** Refreshes the row prices, the total, the quantity answer and the Add to cart button. */
  #updateRows() {
    const each = this.dataset.eachLabel ?? '[price]';
    let total = 0;
    let count = 0;
    this.#rows.forEach((row, index) => {
      const line = row.cents * row.quantity;
      total += line;
      count += row.quantity;
      const price = this.refs.rows.querySelector(`[data-row="${index}"] [data-row-price]`);
      if (!price) return;
      price.textContent = row.variantId
        ? each.replace('[price]', this.#money(row.cents)) + (row.quantity > 1 ? ` = ${this.#money(line)}` : '')
        : this.dataset.sizePlaceholder ?? '';
    });

    const { total: totalElement, submit } = this.refs;
    const priced = this.#rows.length > 0 && this.#rows.every((row) => row.variantId);
    totalElement.textContent = priced ? this.#money(total) : '—';
    if (this.#rows.length) this.#state.qty = { value: String(count) };
    submit.textContent = (this.dataset.addLabel ?? '').replace('[price]', priced ? this.#money(total) : '').replace(/[\s-]+$/, priced ? '$&' : '');
    submit.disabled = !this.#ready();
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const { back, next, digitize, addRow } = this.refs;

    const tile = target.closest('.hat-builder__tile');
    if (tile instanceof HTMLElement && this.contains(tile)) {
      this.#choose(tile);
      return;
    }

    const chip = target.closest('.apparel-builder__chip');
    if (chip instanceof HTMLElement && !this.#busy) {
      this.#current = Math.max(0, this.#steps().indexOf(chip.dataset.chip ?? ''));
      this.#render(true);
      return;
    }

    if (back.contains(target) || next.contains(target)) {
      if (this.#busy) return;
      this.#current = Math.min(this.#steps().length - 1, Math.max(0, this.#current + (back.contains(target) ? -1 : 1)));
      this.#render(true);
      return;
    }

    if (addRow.contains(target)) {
      // A new row starts as the last row's color, with the size still to choose.
      const last = this.#rows[this.#rows.length - 1];
      if (last) this.#loadRow(this.#rows.length, last.colorId, '', '[data-row-color]');
      return;
    }

    const remove = target.closest('[data-row-remove]');
    if (remove instanceof HTMLElement && !this.#busy) {
      this.#rows.splice(this.#rowIndex(remove), 1);
      this.#buildRows();
      this.#renderChips();
      addRow.focus();
      return;
    }

    const vectorize = target.closest('[data-upload-vectorize]');
    const group = vectorize?.closest('[data-upload]');
    if (vectorize && group instanceof HTMLElement) {
      const upload = this.#uploads[Number(group.dataset.upload)];
      if (upload) upload.vectorize = !upload.vectorize;
      this.#render(false);
      return;
    }

    // Embroidery: swaps the stitch file and PDF for the service plus one artwork file.
    if (digitize?.contains(target)) {
      this.#digitize = !this.#digitize;
      this.#render(false);
    }
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.closest('.apparel-builder__row')) {
      const index = this.#rowIndex(target);
      const row = this.#rows[index];
      if (!row) return;
      if (target instanceof HTMLInputElement) {
        // Leaving the field: show the quantity that will be used (never below 1).
        target.value = String(row.quantity);
      } else if (target instanceof HTMLSelectElement && target.matches('[data-row-color]')) {
        this.#loadRow(index, target.value, row.sizeId, '[data-row-color]');
      } else if (target instanceof HTMLSelectElement && target.matches('[data-row-size]')) {
        this.#loadRow(index, row.colorId, target.value, '[data-row-size]');
      }
      return;
    }

    if (target instanceof HTMLInputElement && target.matches('[data-stock-choice]')) {
      this.#updateRows();
      return;
    }

    if (target instanceof HTMLInputElement && target.type === 'file') {
      // A new file starts without the service.
      const upload = this.#uploads.find((candidate) => candidate.input === target);
      if (upload) upload.vectorize = false;
      this.#render(false);
    }
  };

  /**
   * Typing a quantity or a note updates the page straight away.
   * @param {Event} event
   */
  #onInput = (event) => {
    const input = event.target;
    if (input === this.refs.notes) {
      const notes = this.refs.notes.value.trim();
      this.#state.notes = notes ? { value: notes } : undefined;
      this.#renderChips();
      return;
    }
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-row-quantity]')) return;
    const row = this.#rows[this.#rowIndex(input)];
    if (!row) return;
    row.quantity = Math.max(1, Math.floor(Number(input.value)) || 1);
    this.#updateRows();
    this.#renderChips();
  };

  /**
   * Writes the cart fields, one line per quantity row, then lets the browser post the form
   * (multipart, so the files are uploaded as line item properties). Shopify answers by
   * showing the cart.
   * @param {SubmitEvent} event
   */
  #onSubmit = (event) => {
    if (!this.#ready() || this.#sending) {
      event.preventDefault();
      return;
    }

    const serviceId = this.dataset.serviceVariantId ?? '';
    const services = this.#services();
    const rows = this.#rows;
    // One garment line and no service uses Shopify's plain product fields. Anything more goes as "items".
    const list = rows.length > 1 || services.length > 0;
    const field = (/** @type {number} */ index, /** @type {string} */ name) => (list ? `items[${index}][${name}]` : name);
    const property = (/** @type {number} */ index, /** @type {string} */ name) =>
      list ? `items[${index}][properties][${name}]` : `properties[${name}]`;

    // The file fields this path uses, each with the property name it is saved under.
    const { stitchFile, pdfFile } = this.refs;
    const twoFiles = this.#needsEmbroideryFiles();
    /** @type {[HTMLInputElement, string][]} */
    const files = twoFiles
      ? [stitchFile, pdfFile].map((input) => [input, input.dataset.property ?? ''])
      : this.#uploads.map((upload) => [upload.input, upload.property]);

    const data = this.dataset;
    const stockName = this.refs.stock?.dataset.property;
    const stockChoice = this.#stockChoice();
    const notes = this.refs.notes.value.trim();
    const title = this.#state.model?.value ?? '';

    /** @type {[string, string][]} */
    const values = [];
    rows.forEach((row, index) => {
      values.push([field(index, 'id'), row.variantId], [field(index, 'quantity'), String(row.quantity)]);
      values.push([property(index, data.placementProperty ?? 'Placement'), this.#state.deco?.value ?? '']);
      if (notes) values.push([property(index, this.refs.notes.dataset.property ?? 'Notes'), notes]);
      if (stockName && stockChoice) values.push([property(index, stockName), stockChoice]);
      // A file field uploads once, so the files ride on the first line only.
      if (index > 0) for (const [, name] of files) values.push([property(index, name), data.sameAsFirst ?? '']);
    });
    // One service line per file that needs it.
    services.forEach((location, offset) => {
      const index = rows.length + offset;
      values.push(
        [`items[${index}][id]`, serviceId],
        [`items[${index}][quantity]`, '1'],
        [`items[${index}][properties][For]`, `${title} - ${location}`]
      );
    });

    // Only the files this path asks for get a name, so only they are uploaded.
    for (const input of [stitchFile, pdfFile, ...this.#uploads.map((upload) => upload.input)]) input.name = '';
    for (const [input, name] of files) input.name = property(0, name);

    this.refs.fields.replaceChildren(
      ...values.map(([name, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        return input;
      })
    );
    this.#setSending(true);
  };
}

if (!customElements.get('apparel-builder')) {
  customElements.define('apparel-builder', ApparelBuilder);
}
